import React, { useEffect, useState } from 'react';
import { Card, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { commaSeparatedString } from '../../utils/utils';
import { MinusSquareOutlined, PlusSquareTwoTone, MinusSquareTwoTone  } from '@ant-design/icons'
import { useCurrency } from '../../context/CurrencyContext';

interface DataType {
  key: string;
  summary: string;
  sep24: string | number;
  oct24: string | number;
  nov24: string | number;
  dec24: string | number;
  jan25: string | number;
  feb25: string | number;
  total: string | number;
}

// Define the columns
const columns: ColumnsType<DataType> = [
  {
    title: 'Total Monthly Summary',
    dataIndex: 'summary',
    key: 'summary',
    fixed: 'left',
  },
  {
    title: 'Sep-24',
    dataIndex: 'sep24',
    key: 'sep24',
  },
  {
    title: 'Oct-24',
    dataIndex: 'oct24',
    key: 'oct24',
  },
  {
    title: 'Nov-24',
    dataIndex: 'nov24',
    key: 'nov24',
  },
  {
    title: 'Dec-24',
    dataIndex: 'dec24',
    key: 'dec24',
  },
  {
    title: 'Jan-25',
    dataIndex: 'jan25',
    key: 'jan25',
  },
  {
    title: 'Feb-25',
    dataIndex: 'feb25',
    key: 'feb25',
  },
  {
    title: 'Total',
    dataIndex: 'total',
    key: 'total',
    fixed: 'right',
  },
];

// Define the data
const data: DataType[] = [
  {
    key: '1',
    summary: 'Total Revenue',
    sep24: '$57,297',
    oct24: '$77,093',
    nov24: '$77,093',
    dec24: '$77,093',
    jan25: '$77,093',
    feb25: '$77,093',
    total: '$442,763',
  },
  {
    key: '2',
    summary: 'Total Billable Hours',
    sep24: 486,
    oct24: 639,
    nov24: 639,
    dec24: 639,
    jan25: 639,
    feb25: 639,
    total: 3681,
  },
  {
    key: '3',
    summary: 'Total Cost',
    sep24: '$27,653',
    oct24: '$34,831',
    nov24: '$34,831',
    dec24: '$34,831',
    jan25: '$34,831',
    feb25: '$34,831',
    total: '$201,810',
  },
  {
    key: '4',
    summary: 'Total Client Margin $',
    sep24: '$29,644',
    oct24: '$42,262',
    nov24: '$42,262',
    dec24: '$42,262',
    jan25: '$42,262',
    feb25: '$42,262',
    total: '$240,954',
  },
  {
    key: '5',
    summary: 'Client Margin %',
    sep24: '51.7%',
    oct24: '54.8%',
    nov24: '54.8%',
    dec24: '54.8%',
    jan25: '54.8%',
    feb25: '54.8%',
    total: '54.4%',
  },
];

interface SummaryTableProps {
  title: string;
  monthColumns: string[];
  dataSource?: DataType[];
}
// Incomplete Just the Layout have to make it dynamic
const SummaryTable = ({ title, monthColumns, dataSource = data }: SummaryTableProps) => {
  const { dealCurrency, currencySymbol, updateCurrency } = useCurrency();
  const [columns, setColumns] = useState<any[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<any[]>(['1']);

  const handleExpandAll = () => {
    if(expandedRowKeys.length !== dataSource.length) {
      setExpandedRowKeys(dataSource.map(row => row.key))
    } else {
      setExpandedRowKeys([])
    }
  }

  useEffect(() => {
    const generateColumns = () => {
      const cols = [
        {
          title: <div style={{cursor: 'pointer', display: 'flex', paddingLeft: '4px'}} 
          onClick={() => handleExpandAll()}>
            {expandedRowKeys.length !== dataSource.length ? expandedRowKeys.length == 0 ? <PlusSquareTwoTone />: <MinusSquareOutlined /> : <MinusSquareTwoTone />}
            </div>,
          dataIndex: 'expandControl',
          key: 'expandControl',
          width: 50, // Fixed width for the column
        },
        {
          title: '',
          dataIndex: 'summary',
          key: 'summary',
          // fixed: 'left',
          width: 180, // Fixed width for the column
        },
        {
          title: 'Total',
          dataIndex: 'total',
          key: 'total',
          align: 'right',
          className: 'total-column',
          render: (text: string, record: any) => text ? text.toString().includes('%') ? text : record['summary'] == 'Total Billable Hours' ? commaSeparatedString(text.toString().includes('hr') ? text.split(" hr")[0] : text) : text.toString().includes('hr') ? commaSeparatedString(text.split(" hr")[0]) : `${currencySymbol} ${commaSeparatedString(text)}` : text,
        },
      ];

      const dynamicMonthCols = monthColumns.map((monthTitle, i) => ({
        title: monthTitle,
        dataIndex: `month${i}`,
        key: `month${i}`,
        align: 'right',
        className: 'month-column',
        render: (text: string, record: any) => text ? text.toString().includes('%') ? text : record['summary'] == 'Total Billable Hours' ? commaSeparatedString(text.toString().includes('hr') ? text.split(" hr")[0] : text) : text.toString().includes('hr') ? commaSeparatedString(text.split(" hr")[0]) : `${currencySymbol} ${commaSeparatedString(text)}` : text,
      }));

      return [...cols.slice(0, 2), ...dynamicMonthCols, ...cols.slice(-1)];
    };

    setColumns(generateColumns());
  }, [monthColumns, expandedRowKeys]);
  return (
    <Card title={title} style={{ marginBottom: '16px' }}>
      <div style={{ width: '100%', overflowY: 'auto' }}>
        <Table
          className='monthly-summary-table'
          dataSource={dataSource.filter((row) => row.summary !== `Total Target Margin ${currencySymbol}` && row.summary !== 'Total Target Revenue' && row.summary !== 'Target Margin %')}
          columns={columns}
          expandable={{ 
            expandedRowKeys: expandedRowKeys,
            onExpand: (expanded, record) => {
              if(expanded) {
                setExpandedRowKeys([...expandedRowKeys, record.key])
              } else {
                setExpandedRowKeys(expandedRowKeys.filter((k)=> k!=record.key))
              }
            }
          }}
          rowKey="key"
          pagination={false}
          size="small"
          scroll={{ x: 1000 }}
          style={{ marginBottom: '16px' }}
        />
      </div>
    </Card>
  );
};

export default SummaryTable;